<template>
  <div class="load-order-form">
    <header class="page-header fade-in">
      <button class="btn btn-light" @click="goBack" :disabled="submitting">
        <i class="bi bi-arrow-left"></i>
        <span>Retour</span>
      </button>
      <div>
        <h1>Modifier l'ordre de chargement</h1>
        <p>
          Mettez à jour les détails du bon de charge et alignez votre plan
          logistique sur les dernières informations.
        </p>
      </div>
    </header>

    <section class="page-body fade-in" v-if="!initialLoading">
      <form class="form-grid" @submit.prevent="submit">
        <div class="form-group">
          <label for="reference">Référence *</label>
          <input
            id="reference"
            v-model.trim="form.reference"
            type="text"
            class="form-control"
            :class="{ 'has-error': errors.reference }"
            placeholder="LOAD-2025-001"
            disabled
          />
          <p v-if="errors.reference" class="error">{{ errors.reference }}</p>
        </div>

        <div class="form-group">
          <label for="customer">Référence client *</label>
          <input
            id="customer"
            v-model.trim="form.customer_reference"
            type="text"
            class="form-control"
            :class="{ 'has-error': errors.customer_reference }"
            placeholder="CUST-001"
          />
          <p v-if="errors.customer_reference" class="error">
            {{ errors.customer_reference }}
          </p>
        </div>

        <div class="form-group">
          <label for="status">Statut</label>
          <select
            id="status"
            v-model="form.status"
            class="form-control"
            :class="{ 'has-error': errors.status }"
          >
            <option
              v-for="statusOption in statusOptions"
              :key="statusOption.value"
              :value="statusOption.value"
            >
              {{ statusOption.label }}
            </option>
          </select>
          <p v-if="errors.status" class="error">{{ errors.status }}</p>
        </div>

        <div class="form-group">
          <label for="scheduledDate">Date prévue *</label>
          <input
            id="scheduledDate"
            v-model="form.scheduled_date"
            type="date"
            class="form-control"
            :class="{ 'has-error': errors.scheduled_date }"
          />
          <p v-if="errors.scheduled_date" class="error">
            {{ errors.scheduled_date }}
          </p>
        </div>

        <div class="form-group">
          <label for="totalAmount">Total prévu (MAD)</label>
          <input
            id="totalAmount"
            v-model.number="form.total_amount"
            type="number"
            min="0"
            step="0.01"
            class="form-control"
            :class="{ 'has-error': errors.total_amount }"
            placeholder="0.00"
          />
          <p v-if="errors.total_amount" class="error">
            {{ errors.total_amount }}
          </p>
        </div>

        <div class="form-group full">
          <label for="notes">Notes</label>
          <textarea
            id="notes"
            v-model.trim="form.notes"
            class="form-control textarea"
            rows="4"
            placeholder="Ajoutez des instructions pour le chargement ou la livraison…"
          ></textarea>
        </div>

        <div class="summary-card">
          <div>
            <span>Date prévue</span>
            <strong>{{ formatDate(form.scheduled_date) }}</strong>
          </div>
          <div>
            <span>Total prévu</span>
            <strong>{{ formatCurrency(form.total_amount) }}</strong>
          </div>
          <div>
            <span>Statut</span>
            <strong>{{ currentStatusLabel }}</strong>
          </div>
        </div>

        <div class="form-actions">
          <button
            type="button"
            class="btn btn-light"
            @click="goBack"
            :disabled="submitting"
          >
            Annuler
          </button>
          <button
            type="submit"
            class="btn btn-primary"
            :disabled="submitting || !canSubmit"
          >
            <i class="bi bi-check-circle"></i>
            <span v-if="!submitting">Mettre à jour l'ordre</span>
            <span v-else>Enregistrement…</span>
          </button>
        </div>
      </form>
    </section>

    <section class="loading-state" v-else>
      <div class="loader"></div>
      <p>Chargement des données…</p>
    </section>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "LoadOrderEdit",
  data() {
    return {
      initialLoading: true,
      submitting: false,
      form: {
        reference: "",
        customer_reference: "",
        status: "draft",
        scheduled_date: "",
        total_amount: 0,
        notes: "",
      },
      errors: {},
      statusOptions: [
        { value: "draft", label: "Brouillon" },
        { value: "in_progress", label: "En cours" },
        { value: "completed", label: "Terminé" },
        { value: "cancelled", label: "Annulé" },
      ],
    };
  },
  computed: {
    ...mapGetters("loadOrder", {
      current: "getCurrent",
    }),
    canSubmit() {
      return (
        this.form.reference.trim().length > 0 &&
        this.form.customer_reference.trim().length > 0 &&
        !!this.form.scheduled_date
      );
    },
    currentStatusLabel() {
      const match = this.statusOptions.find(
        (status) => status.value === this.form.status
      );
      return match ? match.label : this.form.status || "Brouillon";
    },
  },
  watch: {
    current: {
      immediate: true,
      handler(value) {
        if (!value) return;
        this.populateForm(value);
      },
    },
  },
  methods: {
    goBack() {
      this.$router.back();
    },
    populateForm(value) {
      this.form.reference =
        value.reference || value.code || value.uuid || value.id || "";
      this.form.customer_reference =
        value.customer_reference ||
        value.customerReference ||
        value.customer?.reference ||
        value.customer?.id ||
        "";
      this.form.status = value.status || value.state || "draft";
      this.form.scheduled_date =
        value.scheduled_date || value.scheduledDate || value.due_date || "";
      const amount =
        value.total_amount != null
          ? value.total_amount
          : value.totalAmount != null
          ? value.totalAmount
          : value.total != null
          ? value.total
          : value.amount != null
          ? value.amount
          : 0;
      this.form.total_amount = Number(amount) || 0;
      this.form.notes = value.notes || value.comment || "";
    },
    async loadEntry() {
      this.initialLoading = true;
      try {
        const reference = this.$route.params.reference;
        if (!reference) {
          this.$toast?.error?.("Référence du bon de charge manquante.");
          this.$router.replace({ name: "loadorders-list" });
          return;
        }
        await this.$store.dispatch("loadOrder/show", reference);
      } catch (error) {
        console.error("Erreur lors du chargement du bon de charge", error);
        this.$toast?.error?.("Impossible de charger le bon de charge.");
        this.$router.replace({ name: "loadorders-list" });
      } finally {
        this.initialLoading = false;
      }
    },
    validate() {
      const errors = {};
      if (!this.form.reference) {
        errors.reference = "La référence est obligatoire.";
      }
      if (!this.form.customer_reference) {
        errors.customer_reference = "La référence client est obligatoire.";
      }
      if (!this.form.scheduled_date) {
        errors.scheduled_date = "La date prévue est obligatoire.";
      }
      if (
        this.form.total_amount !== null &&
        this.form.total_amount !== "" &&
        Number(this.form.total_amount) < 0
      ) {
        errors.total_amount = "Le montant doit être positif.";
      }
      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
    async submit() {
      if (this.submitting) return;
      if (!this.validate()) return;
      this.submitting = true;
      try {
        const payload = {
          reference: this.form.reference,
          customer_reference: this.form.customer_reference,
          status: this.form.status,
          scheduled_date: this.form.scheduled_date,
          total_amount: Number(this.form.total_amount) || 0,
          notes: this.form.notes,
        };
        await this.$store.dispatch("loadOrder/update", {
          reference: this.form.reference,
          data: payload,
        });
        this.$toast?.success?.("Bon de charge mis à jour avec succès.");
        this.$router.push({ name: "loadorders-list" });
      } catch (error) {
        console.error("Erreur lors de la mise à jour du bon de charge", error);
        this.$toast?.error?.("Impossible de mettre à jour le bon de charge.");
      } finally {
        this.submitting = false;
      }
    },
    formatCurrency(value) {
      const number = Number(value);
      if (Number.isNaN(number)) return value || "—";
      return number.toLocaleString("fr-FR", {
        style: "currency",
        currency: "MAD",
      });
    },
    formatDate(value) {
      if (!value) return "—";
      try {
        return new Date(value).toLocaleDateString("fr-FR", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      } catch (error) {
        return value;
      }
    },
  },
  mounted() {
    this.loadEntry();
  },
  beforeDestroy() {
    this.$store.dispatch("loadOrder/resetCurrent");
  },
};
</script>

<style scoped>
.load-order-form {
  min-height: 100vh;
  background: linear-gradient(180deg, #f8fbff 0%, #eef2ff 100%);
  padding: 2rem 2.5rem 4rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.page-header {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.page-header h1 {
  margin: 0 0 0.35rem;
  font-size: 2.15rem;
  font-weight: 700;
  color: #0f172a;
}

.page-header p {
  margin: 0;
  color: #556073;
}

.page-body {
  background: #ffffff;
  border-radius: 24px;
  padding: 2rem;
  box-shadow: 0 24px 45px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.25rem 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group.full {
  grid-column: 1 / -1;
}

.form-group label {
  font-weight: 600;
  color: #475569;
}

.form-control {
  border-radius: 12px;
  border: 1px solid #d1d5db;
  padding: 0.7rem 1rem;
  font-size: 1rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.form-control:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.form-control:disabled {
  background: #f3f4f6;
  color: #94a3b8;
}

.textarea {
  resize: vertical;
  min-height: 120px;
}

.summary-card {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-radius: 18px;
  background: linear-gradient(
    135deg,
    rgba(37, 99, 235, 0.1),
    rgba(79, 70, 229, 0.12)
  );
  color: #1e293b;
}

.summary-card span {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #475569;
}

.summary-card strong {
  font-size: 1.25rem;
}

.form-actions {
  grid-column: 1 / -1;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.1rem;
  border-radius: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.btn:hover {
  transform: translateY(-1px);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-light {
  background: rgba(148, 163, 184, 0.15);
  color: #1f2937;
}

.btn-primary {
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  color: white;
  box-shadow: 0 10px 22px rgba(79, 70, 229, 0.25);
}

.error {
  color: #ef4444;
  font-size: 0.83rem;
}

.has-error {
  border-color: #ef4444;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 60vh;
  color: #6b7280;
}

.loader {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 4px solid #d1d5db;
  border-top-color: #2563eb;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 992px) {
  .load-order-form {
    padding: 1.5rem 1.5rem 4rem;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-actions {
    flex-direction: column-reverse;
    align-items: stretch;
  }

  .btn {
    justify-content: center;
  }
}

@media (max-width: 768px) {
  .load-order-form {
    padding: 1.25rem 1rem 4rem;
  }

  .page-body {
    padding: 1.5rem;
  }
}
</style>
